import { useIDEState } from '@/hooks/use-ide-state';
import { FileData } from '@/types';
import { ControlledMenu, MenuItem } from '@szhsin/react-menu';
import React, { useCallback, useState } from 'react';

interface FileContextMenuProps {
    file: FileData;
    projectId: string;
    projectName: string;
    children: React.ReactNode;
}

export const FileContextMenu: React.FC<FileContextMenuProps> = React.memo(
    ({ file, projectId, projectName, children }) => {
        const { openFile, selectFile, addOutputMessage } = useIDEState();
        const [isOpen, setIsOpen] = useState(false);
        const [anchorPoint, setAnchorPoint] = useState({ x: 0, y: 0 });

        const handleContextMenu = useCallback((event: React.MouseEvent) => {
            event.preventDefault();
            setAnchorPoint({ x: event.clientX, y: event.clientY });
            setIsOpen(true);
        }, []);

        const handleClose = useCallback(() => {
            setIsOpen(false);
        }, []);

        const handleOpen = useCallback(() => {
            openFile(file.id, projectId);
            selectFile(file.id, projectId);
            setIsOpen(false);
        }, [openFile, selectFile, file.id, projectId]);

        const handleCopyPath = useCallback(async () => {
            const path = `/${projectName}/${file.name}`;
            const fullUrl = `${window.location.origin}${path}`;
            try {
                await navigator.clipboard.writeText(fullUrl);
                addOutputMessage(`Copied path to clipboard: ${fullUrl}`, 'debug');
            } catch (error) {
                console.error('Failed to copy path:', error);
                addOutputMessage(`Failed to copy path: ${error}`, 'error');
            }
            setIsOpen(false);
        }, [projectName, file.name, addOutputMessage]);

        return (
            <div onContextMenu={handleContextMenu}>
                {children}
                <ControlledMenu
                    anchorPoint={anchorPoint}
                    state={isOpen ? 'open' : 'closed'}
                    onClose={handleClose}
                    menuClassName='szh-menu'
                >
                    <MenuItem onClick={handleOpen} className='szh-menu__item'>
                        <span className='mr-2'>📂</span>
                        Open
                    </MenuItem>
                    <MenuItem onClick={handleCopyPath} className='szh-menu__item'>
                        <span className='mr-2'>📋</span>
                        Copy Path
                    </MenuItem>
                </ControlledMenu>
            </div>
        );
    }
);
